import classNames from 'classnames';

/**
 * This is the current major OPUX version which is used
 * to scope OPUX styles based on current version.
 * This allows environments to provide multiple OPUX versions
 * without their styles colliding.

 * Update this class name string when major version upgrades are done for OPUX.

 * Note that this string must match the `opux-version-class` variable defined in
 * [/packages/utilities/src/styles/opux-version.scss](/packages/utilities/src/styles/opux-version.scss)
 */
export const OPUX_STYLES_VERSION = 'opux-v6';
export const buildVersionedClass = function buildVersionedClass(base) {
  if (base === void 0) {
    base = '';
  }
  return OPUX_STYLES_VERSION + "__" + base;
};

/**
 * A wrapper around the classNames library to ease the pain of
 * versioning every single class name in OPUX components.
 *
 * Please note that there are limitations in the implementation of this utility and thus
 * the API isn't exactly the same as Classnames' API is. For example the second
 * parameter is never versioned as that's usually something adopters
 * provide to the components by themselves. Also arrays aren't acceptable
 * parameters for this utility function.
 *
 * "otherParams" rest operator is needed to comply as closely with
 * the Classnames library API as possible.
 *
 * @param base string
 * @param otherParams See the Classnames library for more details: https://github.com/JedWatson/classnames#usage
 * @returns versioned class name string
 */
export const buildVersionedClassNames = function buildVersionedClassNames(base) {
  for (var _len = arguments.length, otherParams = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    otherParams[_key - 1] = arguments[_key];
  }
  const classNameString = classNames(base, ...otherParams);
  // Version the base class
  let versionedClassNameString = classNameString.replace(base, buildVersionedClass(base));
  const firstOtherParam = otherParams[0];
  const isFirstOtherParamObject = firstOtherParam !== null && typeof firstOtherParam === 'object';
  const secondOtherParam = otherParams[1];
  const isSecondOtherParamObject = secondOtherParam !== null && typeof secondOtherParam === 'object';
  let conditionalClasses = {};
  if (isFirstOtherParamObject) {
    conditionalClasses = firstOtherParam;
  } else if (isSecondOtherParamObject) {
    conditionalClasses = secondOtherParam;
  }

  // Version all of the provided "conditional" classes
  Object.keys(conditionalClasses).forEach(conditionalClass => {
    versionedClassNameString = versionedClassNameString.replace(conditionalClass, buildVersionedClass(conditionalClass));
  });
  return versionedClassNameString;
};

/**
 * Function that builds versioned class names to be used in the tests of the components.
 *
 * Many tests use querySelector() or querySelectorAll(). Any style class parameters given to them must have a . prefix
 * which the buildVersionedClass() or buildVersionedClassNames() do not handle correctly. This function fixes the issue and
 * is only to be used within the test files.
 *
 * If multiple classes are provided with the parameter at once, the function also versions them without needing to provide
 * a base class like for the buildVersionedClassNames().
 *
 * @param classes string
 * @returns versioned class(es) in a string
 */

export function buildVersionedClassNamesForTests(classes) {
  const classArr = classes.split(' ');
  let versionedClasses = '';
  classArr.forEach(elem => {
    if (classes.indexOf('.') > -1) {
      const newElem = elem.replace('.', '');
      versionedClasses += " ." + buildVersionedClass(newElem);
    } else {
      versionedClasses += " " + buildVersionedClass(elem);
    }
  });
  return versionedClasses.trim();
}